import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'

export interface Submission {
  _id: string
  nome: string
  email: string
  whatsapp: string
  areasTravadas: string
  padraoRepetitivo: string
  tentativasResolucao: string
  nivelComprometimento: string
  disponibilidadeInvestir: string
  umaCoisa: string
  motivacaoAgora: string
  status: 'Pendente' | 'Contato Realizado' | 'Aprovada para Conversa' | 'Não Aprovada'
  createdAt: string
  updatedAt: string
}

export function useSubmissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)

  const fetchSubmissions = async (filters?: {
    search?: string
    dateFrom?: string
    dateTo?: string
  }) => {
    setLoading(true)
    try {
      const filter: any = {}
      
      if (filters?.search) {
        filter.$or = [
          { nome: { $regex: filters.search, $options: 'i' } },
          { email: { $regex: filters.search, $options: 'i' } },
          { whatsapp: { $regex: filters.search, $options: 'i' } }
        ]
      }
      
      if (filters?.dateFrom || filters?.dateTo) {
        filter.createdAt = {}
        if (filters.dateFrom) {
          filter.createdAt.$gte = filters.dateFrom
        }
        if (filters.dateTo) {
          filter.createdAt.$lte = filters.dateTo
        }
      }

      const { list, total: totalCount } = await lumi.entities.preselection_submissions.list({
        filter,
        sort: { createdAt: -1 },
        limit: 100
      })
      
      setSubmissions(list)
      setTotal(totalCount)
      console.log('Submissions fetched:', list.length)
    } catch (error) {
      console.error('Failed to fetch submissions:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id: string, status: Submission['status']) => {
    try {
      await lumi.entities.preselection_submissions.update(id, {
        status,
        updatedAt: new Date().toISOString()
      })
      await fetchSubmissions()
      console.log('Status updated:', id, status)
    } catch (error) {
      console.error('Failed to update status:', error)
      throw error
    }
  }

  const deleteSubmission = async (id: string) => {
    try {
      await lumi.entities.preselection_submissions.delete(id)
      await fetchSubmissions()
      console.log('Submission deleted:', id)
    } catch (error) {
      console.error('Failed to delete submission:', error)
      throw error
    }
  }

  const exportToCSV = () => {
    const headers = [
      'Data/Hora',
      'Nome',
      'E-mail',
      'WhatsApp',
      'Status',
      'Áreas Travadas',
      'Padrão Repetitivo',
      'Tentativas Resolução',
      'Nível Comprometimento',
      'Disponibilidade Investir',
      'Uma Coisa',
      'Motivação Agora'
    ]

    const rows = submissions.map(sub => [
      new Date(sub.createdAt).toLocaleString('pt-BR'),
      sub.nome,
      sub.email,
      sub.whatsapp,
      sub.status,
      sub.areasTravadas,
      sub.padraoRepetitivo,
      sub.tentativasResolucao,
      sub.nivelComprometimento,
      sub.disponibilidadeInvestir,
      sub.umaCoisa,
      sub.motivacaoAgora
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `preselecao_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  useEffect(() => {
    fetchSubmissions()
  }, [])

  return {
    submissions,
    loading,
    total,
    fetchSubmissions,
    updateStatus,
    deleteSubmission,
    exportToCSV
  }
}
